'use client'

import type { FC } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import FieldWrapper from './FieldWrapper'
import type { FieldRendererProps } from './types'

export const stepTextArrayFieldConfig = {
    getInitialValue: (field: TFormField) => field.defaultValue ?? [{ text: '' }],
    getNewArrayItem: () => ({ text: '' }),
    extraPropsKind: 'array' as const
} satisfies Partial<import('./types').FieldTypeConfig>

const StepTextArrayField: FC<FieldRendererProps> = ({
    field,
    value,
    error,
    formState,
    onArrayChange,
    onAddArrayItem,
    onRemoveArrayItem
}) => {
    const items = (value || []) as { text?: string }[]
    const arr = (formState?.[field.name] ?? items) as unknown[]
    const placeholderTemplate = field.placeholder || 'Step {index} text'
    const maxLength = field.maxLength
    const itemLabelPlural = field.itemLabelPlural || 'step texts'
    const addButtonLabel = field.addButtonLabel || 'Add Step Text'

    return (
        <FieldWrapper fieldName={field.name} error={error}>
            <div className='space-y-4'>
                <Label>{field.label}</Label>
                {items.map((item: typeof items[0], index: number) => {
                    const placeholderText = placeholderTemplate.replace('{index}', String(index + 1))
                    return (
                        <div key={index} className='flex items-center space-x-2'>
                            <div className='flex-1'>
                                <Input
                                    placeholder={placeholderText}
                                    value={item?.text ?? ''}
                                    onChange={e => {
                                        const rawValue = e.target.value || ''
                                        const newValue = maxLength
                                            ? rawValue.slice(0, maxLength)
                                            : rawValue
                                        onArrayChange?.(field.name, index, field, {
                                            ...item,
                                            text: newValue
                                        })
                                    }}
                                />
                            </div>
                            <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                onClick={() => onRemoveArrayItem?.(field.name, index, field)}
                                disabled={!!field.min && arr.length <= field.min}
                                className='h-8 w-8'
                            >
                                <Trash2 className='h-4 w-4' />
                            </Button>
                        </div>
                    )
                })}
                <div className='flex items-items mt-4 gap-2 flex-col'>
                    <Button
                        type='button'
                        onClick={() => onAddArrayItem?.(field.name, field)}
                        disabled={!!field.max && arr.length >= field.max}
                        variant='outline'
                    >
                        {addButtonLabel}
                    </Button>
                    <span>
                        {!!field.min && arr.length < field.min && (
                            <p className='text-destructive text-sm'>
                                Minimum {field.min} {itemLabelPlural}
                            </p>
                        )}
                        {field.max && arr.length >= field.max && (
                            <p className='text-warning text-sm'>
                                Maximum {field.max} {itemLabelPlural} allowed
                            </p>
                        )}
                    </span>
                </div>
            </div>
        </FieldWrapper>
    )
}

export default StepTextArrayField
